import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIncidents, useIncidentSummary } from '../../hooks/useIncidents';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Skeleton } from '../common/Skeleton';
import { Stat } from '../common/Stat';
import { IncidentRow } from './IncidentRow';
import { formatDurationSeconds } from '../../utils/series';

const RANGES = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

export function IncidentsPage() {
  useDocumentTitle('Incidents');
  const [params, setParams] = useSearchParams();
  const days = Number(params.get('days')) || 30;

  const { data: summary, isLoading: summaryLoading } = useIncidentSummary(days);
  const { data: incidents, isLoading } = useIncidents({ days, limit: 200 });

  const setDays = (d: number) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('days', String(d));
      return next;
    }, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Incidents</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every time a service left and returned to a healthy state.
          </p>
        </div>
        <div className="flex gap-1" role="group" aria-label="Time range">
          {RANGES.map(r => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? 'primary' : 'secondary'}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryLoading || !summary ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4 space-y-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-7 w-1/2" />
            </Card>
          ))
        ) : (
          <>
            <Stat
              label="Incidents"
              value={String(summary.total)}
              note={
                summary.ongoing === 0
                  ? 'all resolved'
                  : summary.acknowledged > 0
                    ? `${summary.ongoing} open · ${summary.acknowledged} acknowledged`
                    : `${summary.ongoing} open`
              }
              alarm={summary.ongoing > 0}
            />
            <Stat
              label="Mean time to recovery"
              value={formatDurationSeconds(summary.mttr_seconds)}
              note={`over ${summary.resolved} resolved`}
            />
            <Stat
              label="Longest outage"
              value={formatDurationSeconds(summary.longest_seconds)}
              note={`last ${summary.window_days} days`}
            />
            <Stat
              label="Total downtime"
              value={formatDurationSeconds(summary.total_downtime_seconds || null)}
              note={summary.worst_service ? `worst: ${summary.worst_service.service_name}` : 'no incidents'}
            />
          </>
        )}
      </div>

      <Card className="divide-y divide-gray-200 dark:divide-gray-700">
        {isLoading ? (
          <div className="p-4 space-y-3" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !incidents?.length ? (
          <div className="p-10 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No incidents in the last {days === 1 ? '24 hours' : `${days} days`}.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              That is the good outcome.
            </p>
          </div>
        ) : (
          incidents.map(incident => <IncidentRow key={incident.id} incident={incident} />)
        )}
      </Card>
    </div>
  );
}
